import PropTypes from 'prop-types';
import { useState } from 'react';
import { ExpandMore, WarningAmberRounded } from '@mui/icons-material';
import { Box, Button, Collapse, Dialog, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

const TITLE_ID = 'nfse-avulsa-recusa-title';

// A recusa que a tradução (T4) não conhece chega sem texto para leigo: o caminho é o suporte,
// com o código cru nos detalhes.
const SEM_TRADUCAO = {
  oQueAconteceu:
    'A prefeitura recusou a nota com uma mensagem que ainda não sabemos explicar em palavras simples.',
  oQueFazer:
    'Fale com o suporte da Cakto e mostre os detalhes técnicos abaixo. Se souber o que corrigir, use "Corrigir e emitir de novo".',
};

export const ACAO_RECUSA = {
  corrigirCadastro: 'corrigir_cadastro',
  corrigirEndereco: 'corrigir_endereco',
  emitirDeNovo: 'emitir_de_novo',
  suporte: 'suporte',
};

// O motor não recebe endereço do tomador, e o formulário não tem onde corrigi-lo: a recusa pelo
// endereço vira orientação, com o caminho do suporte.
const ENDERECO_SEM_CORRECAO =
  'O endereço de quem contratou ainda não pode ser informado na nota avulsa. Fale com o suporte da Cakto e mostre os detalhes técnicos abaixo.';

/**
 * Os botões de cada ação da recusa. `principal` é o que resolve; `fechar` é o rótulo de quem
 * só leu; `corrigir` oferece o formulário preenchido como segunda opção.
 */
const BOTOES_DA_ACAO = {
  [ACAO_RECUSA.corrigirCadastro]: { principal: 'Corrigir o cadastro', fechar: 'Agora não' },
  [ACAO_RECUSA.emitirDeNovo]: { principal: 'Emitir de novo', fechar: 'Agora não' },
  [ACAO_RECUSA.corrigirEndereco]: { principal: null, fechar: 'Entendi' },
  [ACAO_RECUSA.suporte]: { principal: null, fechar: 'Entendi', corrigir: true },
};

const BOTOES_PADRAO = { principal: 'Corrigir e emitir de novo', fechar: 'Agora não' };

const nomeDoTomador = (nota) => nota?.tomador?.razao_social || 'o seu cliente';

const descreverAcao = (nota) => {
  const recusa = nota?.recusa;
  if (!recusa?.conhecido) return ACAO_RECUSA.suporte;
  return recusa.acao ?? null;
};

/** O texto da recusa, com a tradução do back quando ela existe. */
export const descreverRecusa = (nota) => {
  const recusa = nota?.recusa ?? null;
  const [erroCru] = nota?.erros ?? [];
  const conhecido = Boolean(recusa?.conhecido && recusa?.o_que_aconteceu);

  return {
    titulo: recusa?.titulo || `A prefeitura recusou a nota para ${nomeDoTomador(nota)}`,
    oQueAconteceu: conhecido ? recusa.o_que_aconteceu : SEM_TRADUCAO.oQueAconteceu,
    oQueFazer:
      (recusa?.acao === ACAO_RECUSA.corrigirEndereco && ENDERECO_SEM_CORRECAO) ||
      (conhecido && recusa?.o_que_fazer ? recusa.o_que_fazer : SEM_TRADUCAO.oQueFazer),
    acao: recusa?.acao ?? null,
    campo: recusa?.campo ?? null,
    codigo: recusa?.codigo ?? erroCru?.codigo ?? null,
    mensagemOriginal: recusa?.mensagem_original ?? erroCru?.mensagem ?? null,
  };
};

function Bloco({ titulo, texto }) {
  return (
    <Stack spacing={0.5}>
      <Typography variant="subtitle1" component="h3">
        {titulo}
      </Typography>
      <Typography variant="body2">{texto}</Typography>
    </Stack>
  );
}

Bloco.propTypes = { titulo: PropTypes.string.isRequired, texto: PropTypes.string.isRequired };

/**
 * A recusa de uma nota avulsa em destaque (RN06): o que aconteceu, o que fazer e a ação que
 * resolve. Âmbar, e não vermelho: o produtor tem o que fazer, e vermelho lê como "deu tudo errado".
 */
export function RecusaNotaAvulsaDialog({
  open,
  nota,
  outrasRecusadas = 0,
  onClose,
  onCorrigir,
  onCorrigirCadastro,
}) {
  const [detalhesAbertos, setDetalhesAbertos] = useState(false);

  if (!nota) return null;

  const recusa = descreverRecusa(nota);
  // Sem tradução, o caminho é o suporte, mesmo que a ação não tenha vindo.
  const acao = descreverAcao(nota);
  const botoes = BOTOES_DA_ACAO[acao] ?? BOTOES_PADRAO;
  const principal =
    acao === ACAO_RECUSA.corrigirCadastro ? () => onCorrigirCadastro(recusa.campo) : onCorrigir;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="body"
      aria-labelledby={TITLE_ID}
      PaperProps={{
        sx: {
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
          border: 1,
          borderColor: (theme) => alpha(theme.palette.warning.main, 0.45),
        },
      }}
    >
      <Stack spacing={2.5}>
        <Box
          aria-hidden
          sx={{
            width: 52,
            height: 52,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            color: 'warning.main',
            bgcolor: (theme) => alpha(theme.palette.warning.main, 0.16),
          }}
        >
          <WarningAmberRounded />
        </Box>

        <Typography id={TITLE_ID} variant="h4" component="h2" sx={{ textWrap: 'balance' }}>
          {recusa.titulo}
        </Typography>

        <Bloco titulo="O que aconteceu" texto={recusa.oQueAconteceu} />
        <Bloco titulo="O que fazer" texto={recusa.oQueFazer} />

        {outrasRecusadas > 0 && (
          <Typography variant="body2" color="text.secondary">
            {outrasRecusadas === 1
              ? 'Outra nota avulsa também foi recusada. Ela está na lista de notas, com o status Erro.'
              : `Outras ${outrasRecusadas} notas avulsas também foram recusadas. Elas estão na lista de notas, com o status Erro.`}
          </Typography>
        )}

        {(recusa.codigo || recusa.mensagemOriginal) && (
          <Box>
            <Button
              size="small"
              color="inherit"
              onClick={() => setDetalhesAbertos((aberto) => !aberto)}
              aria-expanded={detalhesAbertos}
              endIcon={
                <ExpandMore
                  sx={{
                    transform: detalhesAbertos ? 'rotate(180deg)' : 'none',
                    transition: '0.2s',
                  }}
                />
              }
              sx={{ textTransform: 'none', px: 0 }}
            >
              Detalhes técnicos para o suporte
            </Button>
            <Collapse in={detalhesAbertos} unmountOnExit>
              <Box sx={{ mt: 1, p: 2, borderRadius: 1, bgcolor: 'background.neutral' }}>
                {recusa.codigo && (
                  <Typography variant="caption" component="p" color="text.secondary">
                    Código {recusa.codigo}
                  </Typography>
                )}
                {recusa.mensagemOriginal && (
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                    {recusa.mensagemOriginal}
                  </Typography>
                )}
              </Box>
            </Collapse>
          </Box>
        )}

        <Stack direction={{ xs: 'column-reverse', sm: 'row' }} justifyContent="flex-end" gap={1.5}>
          {botoes.corrigir && (
            <Button
              variant="text"
              color="inherit"
              onClick={onCorrigir}
              sx={{ textTransform: 'none' }}
            >
              Corrigir e emitir de novo
            </Button>
          )}
          <Button
            variant={botoes.principal ? 'outlined' : 'contained'}
            color={botoes.principal ? 'inherit' : 'primary'}
            onClick={onClose}
            sx={{ textTransform: 'none' }}
          >
            {botoes.fechar}
          </Button>
          {botoes.principal && (
            <Button
              variant="contained"
              color="primary"
              onClick={principal}
              sx={{ textTransform: 'none' }}
            >
              {botoes.principal}
            </Button>
          )}
        </Stack>
      </Stack>
    </Dialog>
  );
}

RecusaNotaAvulsaDialog.propTypes = {
  open: PropTypes.bool,
  nota: PropTypes.object,
  outrasRecusadas: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onCorrigir: PropTypes.func.isRequired,
  onCorrigirCadastro: PropTypes.func.isRequired,
};
